import type React from 'react';

import { InputGroup, InputGroupAddon, InputGroupTextarea } from '@/components/ui/input-group';
import type { TextareaSize } from '@/components/ui/textarea';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { FormFieldLabel } from './form-field-label';
import { FormFieldMessages } from './form-field-messages';
import type { BaseFormFieldProps } from './shared';
import { useFormField } from './shared';

interface TextareaInputProps
  extends Omit<React.ComponentProps<typeof Textarea>, 'size'>,
    BaseFormFieldProps {
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  rightSectionAlign?: 'inline-end' | 'block-end';
  size?: TextareaSize;
}

function TextareaInput({
  className,
  label,
  helperText,
  helperTextPlacement = 'beforeControl',
  id,
  isRequired,
  showAsterisk,
  leftSection,
  rightSection,
  rightSectionAlign = 'block-end',
  errorText,
  invalid,
  size,
  ref,
  ...props
}: TextareaInputProps) {
  const { fieldId, helperId, errorId, describedBy } = useFormField(id, helperText, errorText);
  const isInvalid = invalid || Boolean(errorText);
  const TextareaComponent = leftSection || rightSection ? InputGroupTextarea : Textarea;

  const textareaElement = (
    <TextareaComponent
      aria-required={isRequired}
      aria-describedby={describedBy}
      aria-invalid={isInvalid || undefined}
      id={fieldId}
      ref={ref}
      size={size}
      {...props}
    />
  );

  return (
    <div className={cn('grid w-full gap-1.5', className)}>
      {label && (
        <FormFieldLabel
          htmlFor={fieldId}
          label={label}
          isRequired={isRequired}
          showAsterisk={showAsterisk}
        />
      )}
      {helperText && helperTextPlacement === 'beforeControl' && (
        <FormFieldMessages helperText={helperText} helperId={helperId} />
      )}
      {leftSection || rightSection ? (
        <InputGroup size={size}>
          {leftSection && <InputGroupAddon align="block-start">{leftSection}</InputGroupAddon>}
          {textareaElement}
          {rightSection && (
            <InputGroupAddon align={rightSectionAlign}>{rightSection}</InputGroupAddon>
          )}
        </InputGroup>
      ) : (
        textareaElement
      )}
      {helperText && helperTextPlacement === 'afterControl' && (
        <FormFieldMessages helperText={helperText} helperId={helperId} />
      )}
      {errorText && <FormFieldMessages errorText={errorText} errorId={errorId} />}
    </div>
  );
}

TextareaInput.displayName = 'TextareaInput';

export { TextareaInput };
